import api from '@/factories/api';

import { configureTrackJS } from '@/lib/trackjs';
import { initAbly } from '@/lib/ably';
import { identify } from '@/lib/analytics';
import { getDeviceToken } from '@/firebase';
import {
  askNotificationsPermission,
  isNotificationsEnabled,
} from '@/lib/notification';
import router from '@/router';
import { update as updateIntercom } from '@intercom/messenger-js-sdk';

export default {
  async loadUsers({ commit, getters }, options = false) {
    const schemaFull =
      typeof options === 'object' ? options.schemaFull : options;
    const force = typeof options === 'object' ? options.force : false;

    if (getters.usersLoaded && !force) {
      if (!schemaFull || getters.users.every(({ schema }) => schema === 'full'))
        return;
    }

    commit(`setUsersLoaded`, false);

    try {
      const path = 'users' + (schemaFull ? '' : '/usernames');
      const teamFilterOff = getters.isAdmin;

      const res = await api({ teamFilterOff }).get(path);
      let { users = [], self = {} } = res || {};

      if (self && !users.some(({ id }) => id?.value === self?.id?.value)) {
        users.push(self);
      }

      if (schemaFull) {
        users = convertUsersArr(users);
      }

      commit('setUsers', users);
      return true;
    } catch (err) {
      throw err;
    } finally {
      commit(`setUsersLoaded`, true);
    }
  },

  async loadSingleUser({ commit }, userId) {
    commit('setUserLoading', true);
    try {
      const res = await api().get('users/' + userId);
      commit('setUser', convertUserDataToPlainObject(res));
      return res;
    } catch (err) {
      throw err;
    } finally {
      commit('setUserLoading', false);
    }
  },

  async updateUser({ commit, dispatch }, { data }) {
    const { id } = data || {};
    if (!id) return false;

    commit('setUserLoading', true);
    try {
      await api().put('users/' + id, prepareUserData(data));
      dispatch('loadSingleUser', id);
    } finally {
      commit('setUserLoading', false);
    }
  },

  async updateUserTeam({ getters }, teamId) {
    try {
      const userId = getters.currentUser?.id;

      await api().put('users/' + userId, {
        selected_team: teamId ?? '',
      });

      localStorage.removeItem('teams.current');
    } catch (err) {
      console.log('failed to set user team', err);
    }
  },

  async changeUserCredentials({}, { data, type }) {
    try {
      return await api().put(`users/${type}`, data);
    } catch (err) {
      throw err;
    }
  },

  async reinviteUser({}, userId) {
    try {
      return await api().post(`users/reinvite/${userId}`);
    } catch (err) {
      throw err;
    }
  },

  async createUser({ commit, dispatch, getters }, user) {
    commit('setUserLoading', true);
    try {
      const res = await api().post('users', user);
      const userId = res?.new_user_id || '';

      if (!userId) return;

      // load fresh user
      dispatch('loadSingleUser', userId);

      // update team storage
      const teams = user?.teams || [];
      if (teams.length) {
        dispatch('teams/addNewUserToTeam', { userId, teams }, { root: true });
      }
    } finally {
      commit('setUserLoading', false);
    }
  },

  async removeUser({ commit, dispatch, getters }, { id, params }) {
    try {
      const res = await api().delete(`users/${id}`, { data: params });

      dispatch(
        'teams/updateUserTeams',
        { userId: id, teams: [] },
        { root: true }
      );

      commit('setUser', {
        id,
        status: '0',
        teams: [],
      });

      const { replacement_user_id } = params || {};
      if (replacement_user_id) {
        const data = { oldOwnerId: +id, newOwnerId: +replacement_user_id };
        commit('campaigns/changeCampaignsOwner', data, { root: true });
        commit('changeLessonsOwner', data, { root: true });
      }

      return res;
    } catch (err) {
      throw err;
    }
  },

  async restoreUser({ commit }, userId) {
    try {
      await api().post(`users/restore/${userId}`);
      commit('setUser', {
        id: userId,
        status: '1',
      });
    } catch (err) {
      throw err;
    }
  },

  async loadPermissions({ commit, state }) {
    if (state.allPermissions.length) return;

    try {
      const result = await api().get('users/permissions');
      const permissions = result?.permissions || result;
      commit('setPermissions', Array.isArray(permissions) ? permissions : []);
    } catch (err) {
      throw err;
    }
  },

  async loadCurrentUser({ commit, getters, dispatch }) {
    if (getters.currentUserLoaded) return;

    try {
      const currentUser = await api({}).get('users/current');
      if (!currentUser || Array.isArray(currentUser)) return { failed: true };

      commit('setCurrentUser', currentUser);

      initializeCurrentUserSession({
        currentUser,
        hasAccessToPage: getters.hasAccessToPage,
        initializeMessaging: () => dispatch('initFirebaseMessaging'),
      });
    } finally {
      commit('setCurrentUserLoaded', true);
    }
  },

  async loadUsersTimezones({ commit, getters }) {
    if (getters.usersTimezones) return;

    try {
      const usersTimezones = await api().get('users/timezones');
      commit('setUsersTimezones', usersTimezones);
    } catch (error) {
      throw error;
    }
  },

  async searchTwilioNumbers({}, params) {
    try {
      const numbers = await api().get('twilio/search_phones', { params });
      return numbers || [];
    } catch (error) {
      throw error;
    }
  },

  async purchaseTwilioNumber({}, data) {
    const teamFilterOff = true;
    try {
      const res = await api({ teamFilterOff }).post(
        'twilio/purchase_phone',
        data
      );
      return res;
    } catch (error) {
      throw error;
    }
  },

  setDeviceToken(_, device_token) {
    if (!device_token) return;

    try {
      api().post('users/device_token', { device_token });
    } catch (err) {
      console.log('set device token error', err);
    }
  },

  async removeDeviceToken(_, device_token) {
    if (!device_token) return;

    try {
      await api().delete('users/device_token', {
        data: { device_token },
      });
    } catch (err) {
      console.log('set device token error', err);
    }
  },

  async getCurrentDeviceTokens({ dispatch }) {
    try {
      let tokens = (await api().get('users/device_token')) || [];

      // convert tokens to array
      if (typeof tokens === 'object' && !Array.isArray(tokens)) {
        tokens = Object.values(tokens);
      }

      // fcm docs
      // Your app should resubscribe to topics once per month and/or whenever the registration token changes.
      // If an app instance is idle for 2 months (or your own staleness window) you should unsubscribe it.
      let validTokens = [];

      tokens.forEach((t) => {
        const monthLaterTimestamp =
          28 * 24 * 60 * 60 * 1000 + t.timestamp * 1000;
        if (monthLaterTimestamp > new Date().getTime()) {
          validTokens.push(t);
        } else {
          dispatch('removeDeviceToken', t.token);
        }
      });

      return validTokens;
    } catch (err) {
      console.log('get device tokens error', err);
    }
  },

  async initFirebaseMessaging({ dispatch, getters }) {
    if (!isNotificationsEnabled() || !getters.isUserLoggedIn) return;

    const device_token = await getDeviceToken();
    if (!device_token) {
      console.log('unable to get device token');
      return;
    }

    const currentTokens = await dispatch('getCurrentDeviceTokens');
    const tokenExists = currentTokens.some(
      ({ token }) => token === device_token
    );

    if (!tokenExists) {
      dispatch('setDeviceToken', device_token);
    }
  },

  async loadAchievements({ commit, state }) {
    if (state.achievementsLoaded) return;
    const achievements = await api().get('users/achievements');
    commit('updateAchievement', achievements || []);
    commit('setAchievementsLoaded');
  },

  async sendAchievement({ commit, getters, rootGetters }, event) {
    if (rootGetters.profilePid || getters.hasAchievement(event)) return;
    const achievements = await api().post('users/achievements', { event });
    commit('updateAchievement', achievements || []);
  },
};

// for some reason GET users request returns Boolean value for permissions fields
// but for PUT we need to send them as '1'/'0'
const prepareUserData = (data) => {
  const permissionsFields = [
    'add_contacts',
    'delete_contacts',
    'import_contacts',
  ];
  return Object.keys(data).reduce((acc, i) => {
    acc[i] = permissionsFields.includes(i) ? (data[i] ? '1' : '0') : data[i];
    return acc;
  }, {});
};

const getItemId = (url) => {
  const urlArr = url.split('/');
  return urlArr[urlArr.length - 1];
};

const preparePermissions = (data, permissions) => {
  return {
    label: 'Permissions',
    value: Object.keys(data).reduce((acc, i) => {
      if (i === 'id') return acc;

      if (data[i]) {
        acc.push(i);
      } else {
        acc = acc.filter((p) => p !== i);
      }
      return acc;
    }, permissions),
  };
};

const convertUsersArr = (arr) => {
  return arr.map((i) => convertUserDataToPlainObject(i));
};

const convertUserDataToPlainObject = (user) => {
  let obj = {
    schema: 'full',
  };

  for (let key in user) {
    obj[key] = user[key]?.value;
  }

  obj.name = `${obj.first_name} ${obj.last_name}`;
  obj.initials =
    obj?.intials ||
    obj.name
      .split(' ')
      .map((i) => {
        return i.slice(0, 1);
      })
      .join('');

  return obj;
};

const trackUserInfo = (user) => {
  initAbly(user);

  if (!user?.sudo) {
    identify(user);
    if (user?.intercom_user_jwt) {
      updateIntercom({ intercom_user_jwt: user.intercom_user_jwt });
    }
  }
};

const initializeCurrentUserSession = ({
  currentUser,
  hasAccessToPage,
  initializeMessaging,
}) => {
  checkAccessToCurrentRoute(hasAccessToPage);
  configureTrackJS(currentUser.id);
  askNotificationsPermission(initializeMessaging);
  trackUserInfo(currentUser);
};

const checkAccessToCurrentRoute = (hasAccess) => {
  const { meta = {} } = router?.currentRoute || {};
  if (!hasAccess(meta)) {
    router.push('/');
  }
};
